'use client';

import { usePathname } from 'next/navigation';
import { ReactNode, useCallback, useEffect } from 'react';
import { useSound } from 'react-sounds';
import { useInterval } from '@mantine/hooks';
import { refresh } from '@/actions/refresh';
import { UserStoreUser, useUserStore } from '@/shared/stores/userStore';

interface UserProviderProps {
  children: ReactNode;
  initUser: UserStoreUser;
}

const UserProvider = ({ children, initUser }: UserProviderProps) => {
  const { init, setUser, unsetUser, user, socket, isInitialized } = useUserStore((state) => state);
  const soundArcadeCoin = useSound('arcade/coin').play;
  const path = usePathname();

  const updateUser = useCallback(
    async () => {
      if (user) {
        try {
          const state = await refresh();
          if (state.user) {
            if (user.coins < state.user.coins) {
              soundArcadeCoin();
            }
            if (
              user.id === state.user.id &&
              user.email === state.user.email &&
              user.name === state.user.name &&
              user.coins === state.user.coins &&
              user.role === state.user.role &&
              user.avatar === state.user.avatar &&
              user.support.length === state.user.support.length
            ) {
              return;
            }
            setUser(state.user);
          } else {
            unsetUser();
          }
        } catch (e) {}
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  );

  const interval = useInterval(updateUser, path.includes('cabinet') ? 15000 : 60000);

  useEffect(
    () => {
      if (socket) {
        function updateUserEvent() {
          if (user) updateUser();
        }

        socket.on('updateUser', updateUserEvent);
        return () => {
          if (socket) {
            socket.off('updateUser', updateUserEvent);
          }
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [socket, user],
  );

  useEffect(() => {
    if (user && !interval.active) {
      interval.start();
    } else if (interval.active) {
      return interval.stop;
    }
  }, [interval, user]);

  useEffect(
    () => {
      if (!user && !isInitialized) {
        if (initUser) {
          setUser(initUser);
        } else {
          init();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initUser, isInitialized, user],
  );

  return <>{children}</>;
};

export default UserProvider;
